<script setup lang="ts">
// Draws a plan's proposed workflow (see `buildPlanGraph`): the same read-only canvas
// `TaskGraph.vue` already uses (`FlowCanvas` + `TaskGraphNode`/`TaskGraphEdge`), extended with a
// status marker (`data-plan-status`) so a new, changed or removed step is distinguishable at a
// glance (#227).
import { type Edge, MarkerType, type Node, Position, useVueFlow } from '@vue-flow/core'
import { computed, type HTMLAttributes, type SVGAttributes, useId, watch } from 'vue'
import FlowCanvas from '@/components/FlowCanvas.vue'
import TaskGraphEdge from '@/components/TaskGraphEdge.vue'
import TaskGraphNode from '@/components/TaskGraphNode.vue'
import type { PlanGraph, PlanGraphNodeStatus } from '@/lib/planGraph'
import { nodeObstacles, TASK_GRAPH_NODE_HEIGHT } from '@/lib/taskGraph'
import { dataAttributes } from '@/lib/vueFlowAttributes'

const props = defineProps<{ graph: PlanGraph }>()
const id = useId()
const { fitView, onNodesInitialized } = useVueFlow(id)

/** What each marker means, so the picture is readable without a legend in the head. */
const STATUS_LABELS: Record<PlanGraphNodeStatus, string> = {
    new: 'New step',
    changed: 'Changed step',
    removed: 'Removed step',
    unchanged: 'Already there',
}

const STATUS_COLOURS: Record<PlanGraphNodeStatus, string> = {
    new: 'var(--electric-cyan)',
    changed: 'var(--amber, #d98324)',
    removed: 'var(--destructive)',
    unchanged: 'var(--muted-grey)',
}

/**
 * What a marker means when it is on a handoff rather than on a step: a plan can add or drop a
 * handoff between two steps it otherwise leaves alone, and that line needs naming too.
 */
const HANDOFF_LABELS: Partial<Record<PlanGraphNodeStatus, string>> = {
    new: 'New handoff',
    removed: 'Removed handoff',
}

const STATUS_ORDER = ['new', 'changed', 'removed', 'unchanged'] as PlanGraphNodeStatus[]

/**
 * Only the statuses actually on this canvas, in a stable reading order: the step markers first,
 * then any marker that appears only on a handoff, so nothing drawn goes unexplained.
 */
const legend = computed(() => {
    const onNodes = STATUS_ORDER.filter(status => props.graph.nodes.some(node => node.status === status))
    const onEdgesOnly = STATUS_ORDER.filter(
        status => !onNodes.includes(status) && props.graph.edges.some(edge => edge.status === status),
    )
    return [
        ...onNodes.map(status => ({ key: `node-${status}`, status, label: STATUS_LABELS[status] })),
        ...onEdgesOnly.map(status => ({
            key: `edge-${status}`,
            status,
            label: HANDOFF_LABELS[status] ?? STATUS_LABELS[status],
        })),
    ]
})

const nodes = computed<Node[]>(() =>
    props.graph.nodes.map(node => ({
        id: node.slug,
        type: 'task',
        data: node,
        position: node.position,
        draggable: false,
        connectable: false,
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        class: `plan-node plan-node-${node.status}`,
        domAttributes: dataAttributes<HTMLAttributes>({
            'data-plan-status': node.status,
            ...(node.taskId ? { 'data-task-id': node.taskId } : {}),
            ...(node.offCanvas ? { 'data-off-canvas': 'true' } : {}),
        }),
    })),
)

const edges = computed<Edge[]>(() =>
    props.graph.edges.map(edge => {
        // Whether a handoff is proposed, going or already there is `buildPlanGraph`'s reading: it
        // diffs a re-routed task's successors, which a node-status-only view here cannot see.
        const status = edge.status
        const color = STATUS_COLOURS[status]
        const routeY =
            Math.max(
                TASK_GRAPH_NODE_HEIGHT,
                ...props.graph.nodes.map(node => node.position.y + TASK_GRAPH_NODE_HEIGHT),
            ) +
            44 +
            edge.returnLane * 28
        return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: 'workflow',
            data: {
                ...edge,
                showLabel: true,
                routeY,
                muted: false,
                active: false,
                obstacles: nodeObstacles(props.graph.nodes),
            },
            markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color },
            style: {
                stroke: color,
                strokeWidth: 1.5,
                // A handoff the plan proposes is dashed, the way an off-canvas connection already is.
                ...(status === 'unchanged' && !edge.dangling ? {} : { strokeDasharray: '5 5' }),
            },
            domAttributes: dataAttributes<SVGAttributes>({
                'data-edge-source': edge.source,
                'data-edge-target': edge.target,
                'data-edge-status': status,
            }),
        }
    }),
)

function fitCanvas() {
    void fitView({ padding: 0.2, maxZoom: 1, duration: 200 })
}

onNodesInitialized(fitCanvas)
watch(
    () => props.graph.nodes.map(node => `${node.slug}:${node.status}`).join('|'),
    () => fitCanvas(),
)
</script>

<template>
	<div class="plan-graph" data-testid="plan-graph">
		<ul class="plan-graph-legend" aria-label="What each step marker means">
			<li v-for="entry in legend" :key="entry.key" :class="`plan-legend-${entry.status}`">
				<span class="plan-legend-swatch" aria-hidden="true" />{{ entry.label }}
			</li>
		</ul>
		<div class="plan-graph-canvas">
			<FlowCanvas :id="id" :nodes="nodes" :edges="edges" aria-label="Proposed workflow">
				<template #node-task="nodeProps"><TaskGraphNode :data="nodeProps.data" navigation="task" /></template>
				<template #edge-workflow="edgeProps"><TaskGraphEdge v-bind="edgeProps" /></template>
			</FlowCanvas>
		</div>
	</div>
</template>

<style scoped>
.plan-graph {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    border: 1px solid var(--border);
    background: var(--background);
}
.plan-graph-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 0;
    padding: 10px 12px;
    list-style: none;
    border-bottom: 1px solid var(--border);
    background: var(--card);
    font-size: 12px;
    color: var(--muted-foreground);
}
.plan-graph-legend li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.plan-legend-swatch {
    width: 10px;
    height: 10px;
    border: 1px solid currentcolor;
}
.plan-legend-new .plan-legend-swatch {
    background: var(--electric-cyan);
}
.plan-legend-changed .plan-legend-swatch {
    background: #d98324;
}
.plan-legend-removed .plan-legend-swatch {
    background: var(--destructive);
}
.plan-legend-unchanged .plan-legend-swatch {
    background: var(--muted-grey);
}
.plan-graph-canvas {
    position: relative;
    flex: 1;
    min-height: 320px;
    height: 46vh;
    background: color-mix(in srgb, var(--muted) 45%, var(--background));
}
.plan-graph :deep(.plan-node-new .task-graph-node),
.plan-graph :deep(.plan-node-new) {
    outline: 2px solid var(--electric-cyan);
}
.plan-graph :deep(.plan-node-changed) {
    outline: 2px solid #d98324;
}
.plan-graph :deep(.plan-node-removed) {
    outline: 2px dashed var(--destructive);
    opacity: 0.72;
}
.plan-graph :deep(.vue-flow__edge-path) {
    fill: none;
}

@media (max-width: 760px) {
    .plan-graph-canvas {
        height: 52vh;
        min-height: 240px;
    }
}
</style>
